import React, { useState, useEffect, useRef } from 'react';
import { Close, ArrowBack } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import './Chatbox.css';

const ChatWindow = ({
  userRole,
  isOpen,
  setIsOpen,
  selectedUser,
  setSelectedUser,
  selectedUserName,
  setSelectedUserName
}) => {
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);

  const userId = localStorage.getItem('userId');
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!userId) return;

      if (userRole === 'admin') {
        try {
          const res = await fetch(`${API_BASE}/api/messages/chat-users/${userId}`);
          const data = await res.json();
          setUsers(data);
        } catch (err) {
          console.error('Error fetching chat users:', err);
        }
      } else {
        try {
          const res = await fetch(`${API_BASE}/api/messages/admins`);
          const data = await res.json();
          if (data?.[0]) {
            const admin = data[0];
            setSelectedUser(admin._id);
            setSelectedUserName(`${admin.firstName} ${admin.lastName}`);
            fetchConversation(admin._id);
          }
        } catch (err) {
          console.error('Error fetching admin:', err);
        }
      }
    };

    fetchInitialData();
  }, [userRole, userId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!selectedUser || !userId) return;

    const interval = setInterval(() => {
      fetchConversation(selectedUser);
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedUser, userId]);

  const fetchConversation = async (targetId) => {
    if (!userId || !targetId) return;
    try {
      const res = await fetch(`${API_BASE}/api/messages/${userId}/${targetId}`);
      await fetch(`${API_BASE}/api/messages/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, contactId: targetId })
      });
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching conversation:', err);
    }
  };

  const handleSend = async () => {
    if (!message.trim() || !selectedUser) return;

    const msgObj = {
      senderId: userId,
      receiverId: selectedUser,
      senderRole: userRole,
      content: message,
      timestamp: new Date().toISOString(),
    };

    try {
      const res = await fetch(`${API_BASE}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(msgObj)
      });

      if (res.ok) {
        setMessages(prev => [...prev, msgObj]);
        setMessage('');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user.userId);
    setSelectedUserName(user.name || `Entreprise: ${user.userId}`);
    await fetchConversation(user.userId);
    if (userRole === 'admin') {
      try {
        const res = await fetch(`${API_BASE}/api/messages/chat-users/${userId}`);
        const updated = await res.json();
        setUsers(updated);
      } catch (err) {
        console.error('Error updating chat user list after read:', err);
      }
    }
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        {userRole === 'admin' && selectedUser ? (
          <div className="chat-header-back">
            <ArrowBack onClick={() => {
              setSelectedUser(null);
              setMessages([]);
            }} />
            <span>{selectedUserName}</span>
          </div>
        ) : (
          <span>
            {userRole === 'admin' ? 'Boîte de réception' : `Chat avec Admin: ${selectedUserName}`}
          </span>
        )}
        <button className="chat-close-btn" onClick={() => setIsOpen(false)}>
          <Close />
        </button>
      </div>

      {userRole === 'admin' && !selectedUser && (
        <div className="chat-user-list">
          {users.map((u) => (
            <div key={u.userId} className="chat-user-item" onClick={() => handleUserSelect(u)}>
              <div className="chat-user-row">
                <Avatar className="chat-role-avatar">
                  {u.senderRole === 'entreprise' ? 'E' : 'R'}
                </Avatar>
                <div className="chat-user-texts">
                  <div className="chat-user-name-row">
                    <strong className="chat-user-name">{u.name || u.userId}</strong>
                    <span className="chat-user-time">{formatTime(u.lastUpdated)}</span>
                  </div>
                  <div className="chat-user-preview-row">
                    <span className="chat-user-preview">{u.lastMessage}</span>
                    {u.unreadCount > 0 && (
                      <span className="chat-user-unread-badge">{u.unreadCount}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedUser && (
        <>
          <div className="chat-messages">
            {messages.map((m, i) => {
              const isMe = m.senderRole === userRole;
              const senderLabel = m.senderRole === 'entreprise' ? 'E' : m.senderRole === 'recruteur' ? 'R' : 'A';

              return (
                <div key={i} className={`chat-row ${isMe ? 'chat-right' : 'chat-left'}`}>
                  <Avatar className="chat-avatar">{senderLabel}</Avatar>
                  <div className={`chat-bubble ${isMe ? 'me' : 'other'}`}>
                    <div className="chat-content">{m.content}</div>
                    <div className="chat-meta">{formatTime(m.timestamp)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <input
              type="text"
              placeholder="Écrire un message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={!selectedUser}
            />
            <button onClick={handleSend} disabled={!selectedUser || !message.trim()}>
              Envoyer
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWindow;
